import React, { useState } from "react";
import { Modal, Box, Button, Typography, CircularProgress, IconButton, Snackbar, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useUploadSimulationCsvMutation } from "../../../store/services/simulation-service";

SendSimCsvModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

// Mirrors ApplicationUserCsvClassMap. The first five are non-optional in the class map and the
// file fails to parse without them; TenancyId is optional there but mandatory in practice - a
// row without it sends against tenancy 0 and loses the reporting record (BD-810).
const REQUIRED_COLUMNS = [
    "EmailSimulationId",
    "UserIdentifier",
    "UserFirstName",
    "UserLastName",
    "TenancyName",
    "TenancyId"
];

const TEMPLATE_COLUMNS = [
    "EmailSimulationId",
    "UserIdentifier",
    "UserFirstName",
    "UserLastName",
    "TenancyName",
    "TenancyAddress1",
    "TenancyAddress2",
    "TenancyAddress3",
    "TenancyAddress4",
    "TenancyAddress5",
    "TenancyPostcode",
    "TenancyId"
];

const splitCsvLine = (line) => line.split(",").map((cell) => cell.trim().replace(/^"|"$/g, ""));

const validateCsvContent = async (file) => {
    // Strip the BOM Excel writes when saving as "CSV UTF-8", otherwise the first header reads
    // as ﻿EmailSimulationId and gets reported missing.
    const text = (await file.text()).replace(/^﻿/, "");
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");

    if (lines.length === 0) {
        return ["The file is empty."];
    }

    const headers = splitCsvLine(lines[0]);
    const missing = REQUIRED_COLUMNS.filter((required) => !headers.includes(required));

    if (missing.length > 0) {
        return missing.map((column) => `Missing required column: "${column}"`);
    }

    if (lines.length === 1) {
        return ["The file has a header row but no simulations to send."];
    }

    const tenancyIdIndex = headers.indexOf("TenancyId");
    const userIndex = headers.indexOf("UserIdentifier");
    const errors = [];

    lines.slice(1).forEach((line, index) => {
        const cells = splitCsvLine(line);
        const tenancyIdValue = cells[tenancyIdIndex] || "";

        // A blank or non-numeric TenancyId throws inside CsvHelper and kills the whole upload
        // with no feedback in the portal, so catch it here instead.
        if (!/^\d+$/.test(tenancyIdValue) || Number(tenancyIdValue) <= 0) {
            errors.push(`Row ${index + 2} (${cells[userIndex] || "no recipient"}): TenancyId must be a number, got "${tenancyIdValue}"`);
        }
    });

    return errors.slice(0, 20);
};

export default function SendSimCsvModal({ open, onClose }) {
    const theme = useTheme();
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);

    const [csvFile, setCsvFile] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [uploadSimulationCsv] = useUploadSimulationCsvMutation();

    const notify = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        setCsvFile(file);
        setValidationErrors([]);

        if (file) {
            const errors = await validateCsvContent(file);
            if (errors.length > 0) {
                setValidationErrors(errors);
                notify("Please fix the errors in the CSV before sending.", "error");
            }
        }
    };

    const handleDownloadCsv = () => {
        // Pre-fill the tenancy currently being viewed so nobody has to hunt for the id.
        const exampleRow = [
            "Replace with the simulation name",
            "user@example.com",
            "Jane",
            "Doe",
            tenancyName || "Replace with the tenancy name",
            "", "", "", "", "", "",
            tenancyId || "Replace with the tenancy id"
        ];

        const csvContent = `${TEMPLATE_COLUMNS.join(",")}\n${exampleRow.join(",")}`;
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "simulation-send-template.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleUploadCsv = async () => {
        if (!csvFile) {
            notify("Please select a CSV file.", "error");
            return;
        }

        if (validationErrors.length > 0) {
            notify("Please fix the errors in the CSV before sending.", "error");
            return;
        }

        if (csvFile.type !== "application/vnd.ms-excel" && csvFile.type !== "text/csv") {
            notify("Invalid file type. Please upload a CSV file.", "error");
            return;
        }

        setIsUploading(true);

        try {
            const response = await uploadSimulationCsv({ file: csvFile }).unwrap();
            notify(
                response.success ? "Upload successful" : "Error uploading CSV",
                response.success ? "success" : "error");
        } catch (error) {
            console.error("Error uploading CSV:", error);
            notify(error.data || "Error uploading CSV", "error");
        }

        setIsUploading(false);
        onClose();
    };

    const handleClose = () => {
        setCsvFile(null);
        setValidationErrors([]);
        onClose();
    };

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="send-sim-csv-modal-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "80%",
                        maxWidth: 650,
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: theme.borderRadius.sm,
                        p: "26px 32px"
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography id="send-sim-csv-modal-title" variant="h4" gutterBottom>
                            SEND SIMULATIONS VIA CSV
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        The template is pre-filled with the tenancy you are currently viewing
                        {tenancyName ? ` (${tenancyName}, id ${tenancyId})` : ""}. Every row needs a
                        TenancyId, otherwise the send is not recorded against the tenancy.
                    </Typography>

                    <input
                        type="file"
                        accept=".csv,application/vnd.ms-excel,text/csv"
                        onChange={handleFileChange}
                        style={{
                            width: "100%",
                            marginBottom: "20px",
                            border: "2px dashed #000",
                            padding: "10px",
                            borderRadius: "4px"
                        }}
                    />

                    {validationErrors.length > 0 && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Validation Errors
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: "150px", overflowY: "auto" }}>
                                {validationErrors.map((error, index) => (
                                    <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>
                                        {error}
                                    </Typography>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    <Box mt={3} display="flex" justifyContent="flex-start">
                        <Button
                            onClick={handleDownloadCsv}
                            variant="outlined"
                            color="inherit"
                            sx={{ mr: 2, flexGrow: 1 }}
                        >
                            Download CSV Template
                            <DownloadIcon sx={{ ml: 1 }} />
                        </Button>
                        <Button
                            onClick={handleUploadCsv}
                            variant="contained"
                            color="primary"
                            disabled={isUploading || validationErrors.length > 0}
                            sx={{ flexGrow: 1 }}
                        >
                            {isUploading ? <CircularProgress size={24} /> : <>Send Simulations <UploadIcon sx={{ ml: 1 }} /></>}
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
